import { useCallback, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

// 2 seconds — shared default across autosaving editors (agent, document,
// workflow).
export const DEFAULT_AUTO_SAVE_DEBOUNCE_MS = 2000;

type UseDebouncedDirtySaveParams = {
  /** Persist the resource. Called at most once per debounce window. */
  save: () => void;
  /**
   * Guard evaluated at fire time (after the debounce elapses), not at schedule
   * time — state can change during the window (title cleared, focus moved back
   * into the title, edits reverted). Return false to skip this save.
   */
  canSave?: () => boolean;
  /** When false, autosave is disabled and any pending save is cancelled. */
  enabled?: boolean;
  debounceMs?: number;
};

/**
 * Debounced autosave primitive shared by the resource editors. Each editor
 * calls {@link scheduleSave} whenever the user makes a change; the actual save
 * fires once the user pauses for {@link debounceMs}, gated by `enabled` and the
 * optional `canSave` predicate. Any pending save is flushed on unmount so the
 * last edit is never lost.
 */
export function useDebouncedDirtySave({
  save,
  canSave,
  enabled = true,
  debounceMs = DEFAULT_AUTO_SAVE_DEBOUNCE_MS,
}: UseDebouncedDirtySaveParams) {
  const debouncedSave = useDebouncedCallback(() => {
    if (!enabled) {
      return;
    }
    if (canSave !== undefined && !canSave()) {
      return;
    }
    save();
  }, debounceMs);

  useEffect(() => {
    return () => {
      debouncedSave.flush();
    };
  }, [debouncedSave]);

  const scheduleSave = useCallback(() => {
    if (enabled) {
      debouncedSave();
    } else {
      debouncedSave.cancel();
    }
  }, [debouncedSave, enabled]);

  return {
    scheduleSave,
    flushSave: debouncedSave.flush,
    cancelSave: debouncedSave.cancel,
  };
}
